import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FormRow,
  // FormGroup, Label, Input, 
  Select, Button, ButtonGroup, SectionTitle,
  Modal, ModalContent, ModalHeader, CloseButton, Divider,
} from '../../styles/PageStyles';
import ImageCropper from './ImageCropper';
import { createUser, getUserByUserId, register, updateUser } from '../../services/userService';
import toastService from '../../services/toastService';
import { getUserRoles } from '../../services/authService';
import { MultiSelectDropdown } from './MultiSelect';
import { StepContent, StepDescription, StepIcon, StepTitle } from './groupAndUsers';
import { User } from 'lucide-react';
import styled from 'styled-components';
import { MultiSelectDropdownSearch } from './MultiSelectSearch';
import { useToast } from './ToastProvider';


const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.15);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;



const UserDetails = ({ user, onCancelUser, selectedGroup, handleBack }) => {
  const navigate = useNavigate();
  const isNewUser = !Boolean(user.id);
  const [userDetails, setUserDetails] = useState({ 
    first_name: '', 
    last_name: '', 
    gender: 'M', 
    date_of_birth: new Date().toISOString(), 
    company_cd: user?.companyCd || '', 
    status: "INACTIVE", 
    address: '', 
    phone_number: '', 
    bio: '', 
    profile_url: null, 
    username: '', 
    email: '', 
    enabled: true, 
    auth_provider: 'LOCAL',
    roles: [], 
    groups: selectedGroup ? [selectedGroup] : [] 
  });
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedImage] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  // const [roleInput, setRoleInput] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const {showToast} = useToast();

  // Update groups when selectedGroup prop changes
  useEffect(() => {
    if (selectedGroup && isNewUser) {
      setUserDetails(prev => ({
        ...prev,
        groups: [selectedGroup]
      }));
    }
  }, [selectedGroup, isNewUser]);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const response = await getUserRoles();
        console.log("roles", response)
        if (response.length) {
          setUserRoles(response)
        }
      } catch (error) {
        console.log("Error fetchig the user roles:", error)
      }
    }

    const fetchUserDetails = async () => {
      try {
        const response = await getUserByUserId(user.username)
        if ('username' in response ) {
          console.log("resp",response)
          setUserDetails((prev) => {
            const userGroup = selectedGroup ? [selectedGroup] : [];
            const roles = response?.roles || [];
            return { ...prev, ...response, groups: userGroup, roles }
          })
        }
        console.log("user", userDetails)
      } catch (error) {
        console.log("Error fetching user details", error)
      }
    }

    !isNewUser && fetchUserDetails();
    fetchUserRoles();
  }, [])

  const showValidationErrors = (error) => {
    if (error.response && error.response.data && error.response.data.detail) {
      const errors = error.response.data.detail;
      errors?.forEach(validationError => {
        const fieldName = validationError.loc[validationError.loc.length - 1];
        const message = validationError.msg;
        toastService.error(`${fieldName}: ${message}`);
      });
    } else {
      toastService.error('An error occurred while saving the user');
    }
  };

  const updateProfile = async (profileUpdateFlag = false) => {
    if (profileUpdateFlag) {
      userDetails.profile_url = uploadedFileName;
    }
    try {
      // const response = await put(`${PERS_ENDPOINT}`, userDetails, {
      //   withCredentials: true,
      // });

      const response = await updateUser(userDetails)

      setUserDetails(response);

    } catch (error) {
      console.error('Error updating profile:', error);
      navigate('/login');
    }
  };

  const buildRegisterPayload = (userDetails, uploadedFileName) => ({
      username: userDetails.username,
    email: userDetails.email,
    first_name: userDetails.first_name,
    last_name: userDetails.last_name,
    enabled: userDetails.enabled,
    profile_url: uploadedFileName || userDetails.profile_url || "",
    phone_number: userDetails.phone_number,
    address: userDetails.address,
    date_of_birth: userDetails.date_of_birth,
    gender: userDetails.gender,
    auth_provider:userDetails.auth_provider || 'LOCAL',
    bio: userDetails.bio,
    groups: userDetails.groups || [],
    roles: userDetails.roles || []
  });

  const buildUpdatePayload = (userDetails, uploadedFileName) => ({
    ...userDetails,
    profile_url: uploadedFileName || userDetails.profile_url || "",
  });

  const saveUser = async () => {
    try {
      const payload = isNewUser
        ? buildRegisterPayload(userDetails, uploadedFileName)
        : buildUpdatePayload(userDetails, uploadedFileName);

      if(payload?.roles.length == 0) {
        showToast({
          message: "Please select the roles to create a user.",
          type: "error"
        });
        return;
      }
      if (!payload.auth_provider) {
        showToast({
          message: 'Please select an auth provider.',
          type: 'error',
        });

        return;
      }

      const response = isNewUser
        ? await createUser(payload)
        : await updateUser(payload); 

      toastService.success(
        isNewUser
          ? "User registered successfully"
          : "User updated successfully"
      );

      if(handleBack) handleBack();

    } catch (error) {
      console.error(
        `Error ${isNewUser ? "registering" : "updating"} user:`,
        error
      );

      showValidationErrors(error);

      if (!isNewUser && error.response?.status === 401) {
        navigate("/login");
      }
    }
  };


  const handleCropComplete = (croppedFile) => {
    console.log('Uploaded file name:', croppedFile);

  };
  const handleFileUpload = (fileName) => {
    // console.log('handleFileUpload called with:', fileName);
    setUploadedFileName(fileName);
    // console.log('uploadedFileName after setState:', fileName);
    updateProfile(true);
  };

  // const addRole = () => {
  //   if (roleInput.trim() && !(userDetails.roles || []).includes(roleInput.trim())) {
  //     setUserDetails({
  //       ...userDetails,
  //       roles: [...(userDetails.roles || []), roleInput.trim()]
  //     });
  //     setRoleInput('');
  //   }
  // };

  // const removeRole = (roleToRemove) => {
  //   setUserDetails({
  //     ...userDetails,
  //     roles: (userDetails.roles || []).filter(role => role !== roleToRemove)
  //   });
  // };

  // const handleRoleKeyPress = (e) => {
  //   if (e.key === 'Enter') {
  //     e.preventDefault();
  //     addRole();
  //   }
  // };

  const handleRoleChnage = (selectedRoles) => {
    const normalizedRoles = selectedRoles.map((role) => role.name)
    setUserDetails((prev) => {
      return {
        ...prev,
        roles: [...normalizedRoles]
      }
    })
  }

  return (
    <div>
      <SectionTitle>
        {userDetails.first_name ? (
                <>
                <StepIcon>
                        <User />
                      </StepIcon>
                      <StepContent>
                        <StepTitle>{userDetails.first_name}</StepTitle>
                        <StepDescription>{userDetails?.username}</StepDescription>
                </StepContent>
                </>
        ) : (
           <>
                    <StepIcon>
                        <User />
                      </StepIcon>
                      <StepContent>
                        <StepTitle>Add new user</StepTitle>
                        {/* <StepDescription>{step.description}</StepDescription> */}
                </StepContent>
                </>
        )}
      </SectionTitle>
      <Divider />

      <FormRow>
        <FormGroup>
          <Label>First Name</Label>
          <Input 
              type="text" 
              value={userDetails?.first_name || ''}
              placeholder="e.g. John"
              onChange={(e) => setUserDetails({ ...userDetails, first_name: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Last Name</Label>
          <Input 
              type="text" 
              value={userDetails?.last_name || ''}
              placeholder="e.g. Doe"
              onChange={(e) => setUserDetails({ ...userDetails, last_name: e.target.value })} />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label>Gender</Label>
          <Select
            value={userDetails?.gender || ''}
            onChange={(e) => setUserDetails({ ...userDetails, gender: e.target.value })}
          >
            <option value="">Select Gender</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="Other">Other</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Date of Birth</Label>
          <Input
            type="date"
            value={userDetails?.date_of_birth ? userDetails.date_of_birth.split('T')[0] : ''}
            onChange={(e) => {
              const selectedDate = e.target.value;
              const formattedDate = `${selectedDate}T00:00:00Z`;
              setUserDetails({ ...userDetails, date_of_birth: formattedDate });
            }}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup>
          <Label>Email Address</Label>
          <Input 
              disabled={!isNewUser}
              type="email" 
              value={userDetails?.email || ''} 
              placeholder="johndoe@example.com"
              onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label>Phone</Label>
          <Input 
              type="text" 
              value={userDetails?.phone_number || ''} 
              placeholder="+91 7361739811"
              onChange={(e) => setUserDetails({ ...userDetails, phone_number: e.target.value })} />
        </FormGroup>
      </FormRow>

      {/* {isNewUser && ( */}
        <FormRow>
          <FormGroup>
            <Label>Username</Label>
            <Input 
                type="text" 
                disabled={!isNewUser}
                value={userDetails?.username || ''} 
                placeholder="johndoe@example.com"
                onChange={(e) => setUserDetails({ ...userDetails, username: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Auth Provider</Label>
            <Select
              value={userDetails?.auth_provider || 'LOCAL'}
              disabled={!isNewUser}
              onChange={(e) =>
                setUserDetails({
                  ...userDetails,
                  auth_provider: e.target.value,
                })
              }
            >
              <option value="LOCAL">Basic Auth</option>
              <option value="SSO">SSO</option>
              <option value="OAUTH">OAuth</option>
            </Select>
          </FormGroup>
        </FormRow>
          {/* <FormGroup>
            <Label>Roles</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Input 
                  type="text" 
                  value={roleInput} 
                  onChange={(e) => setRoleInput(e.target.value)}
                  onKeyPress={handleRoleKeyPress}
                  placeholder="Enter role and press Enter"
                  style={{ flex: 1 }}
                />
                <Button 
                  type="button"
                  onClick={addRole}
                  style={{ minWidth: '60px', fontSize: '12px', padding: '4px 8px' }}
                >
                  Add
                </Button>
              </div>
              {userDetails.roles && userDetails.roles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {userDetails.roles.map((role, index) => (
                    <span 
                      key={index}
                      style={{
                        background: '#e3f2fd',
                        border: '1px solid #1976d2',
                        borderRadius: '16px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {role}
                      <button
                        type="button"
                        onClick={() => removeRole(role)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </FormGroup> */}
{/* 
              <FormGroup>
                <Label>Roles</Label>
                <MultiSelectDropdown
                  options={userRoles || []}
                  selected={userDetails.roles || []}
                  onChange={(newRoles) => handleRoleChnage(newRoles)}
                />
              </FormGroup>  */}
        <FormRow>
              <FormGroup>
                <Label>Roles</Label>
                <MultiSelectDropdownSearch
                  options={userRoles || []}
                  selected={userDetails.roles.map((r) => ({name: r})) || []}
                  onChange={(newRoles) => handleRoleChnage(newRoles)}
                />
              </FormGroup> 
        
      {/* )} */}

      {/* {isNewUser && selectedGroup && ( */}
          <FormGroup>
            <Label>Group</Label>
            <div style={{
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px 12px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              {selectedGroup} (selected from previous screen)
            </div>
          </FormGroup>
           <FormGroup>
          <Label>Address</Label>
          <Input 
              type="text" 
              value={userDetails?.address || ''} 
              placeholder="e.g. 123 Main St"
              onChange={(e) => setUserDetails({ ...userDetails, address: e.target.value })} />
        </FormGroup>
        </FormRow>
      {/* )} */}

      <FormGroup>
        <Label>Profile Image</Label>
        <Button
          secondary
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowProfileModal(true)}
        >
          Upload Profile Picture
        </Button>
        {showProfileModal && (
          <Modal>
            <ModalContent>
              <ModalHeader>
                <h3>Upload Profile Picture</h3>
                <CloseButton onClick={() => setShowProfileModal(false)}>✖</CloseButton>
              </ModalHeader>
              <Divider />
              <ImageCropper
                uploadedImage={uploadedImage}
                onCropComplete={handleCropComplete}
                onFileUpload={handleFileUpload}
              />
            </ModalContent>
          </Modal>
        )}
      </FormGroup>

      <ButtonGroup>
        <Button
          onClick={() => onCancelUser ? onCancelUser() : navigate(-1)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Cancel
        </Button>
        <Button onClick={saveUser}
          primary
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isNewUser ? 'Register User' : 'Save Changes'}
        </Button>
      </ButtonGroup>
    </div>
  );
};

export default UserDetails;